import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Megaphone, HardHat, ShieldCheck, ArrowRight, ArrowLeft, Check, Clock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Logo } from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { SEND_HOURS } from "@/lib/content";

const ICONS = { communication: Megaphone, batiment: HardHat, numerique: ShieldCheck };
const BUDGETS = [
  { key: "small", label: "Petits marchés", desc: "moins de 90 k€, procédure adaptée" },
  { key: "medium", label: "Marchés moyens", desc: "de 90 k€ à 500 k€" },
  { key: "large", label: "Gros marchés", desc: "plus de 500 k€, procédures formalisées" },
  { key: "any", label: "Tous les montants", desc: "je veux tout voir" },
];

export default function Onboarding() {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [step, setStep] = useState(1);
  const [meta, setMeta] = useState({ niches: [], departements: {} });
  const [company, setCompany] = useState("");
  const [description, setDescription] = useState("");
  const [niche, setNiche] = useState(null);
  const [deps, setDeps] = useState([]);
  const [location, setLocation] = useState("");
  const [budget, setBudget] = useState("any");
  const [frequency, setFrequency] = useState("daily");
  const [sendHour, setSendHour] = useState(8);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/public/niches").then(({ data }) => setMeta(data)).catch(() => {});
  }, []);
  useEffect(() => {
    if (user?.profile) {
      setCompany(user.profile.company_name || "");
      setDescription(user.profile.description || "");
      setNiche(user.profile.niche || null);
      setDeps(user.profile.departements || []);
      setLocation(user.profile.location || "");
      setBudget(user.profile.budget || "any");
      setFrequency(user.frequency || "daily");
      setSendHour(user.send_hour || 8);
    }
  }, [user]);

  const toggleDep = (code) => setDeps((d) => (d.includes(code) ? d.filter((x) => x !== code) : [...d, code]));

  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/profile", {
        company_name: company || "Mon entreprise", description, niche,
        departements: deps, location, budget, alert_email: user?.email,
        alerts_enabled: true, frequency, send_hour: sendHour,
      });
      setUser(data);
      toast.success("Profil enregistré. Bienvenue !");
      const active = ["trialing", "trial", "essentiel", "pro", "premium"].includes(data.plan);
      navigate(active ? "/dashboard" : "/abonnement");
    } catch (e) {
      toast.error("Une erreur est survenue, merci de réessayer.");
    } finally { setSaving(false); }
  };

  const canNext =
    (step === 1 && niche && company.trim()) || step === 2 || step === 3 || step === 4;

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-2xl mx-auto px-5 py-10">
        <div className="flex justify-center mb-8"><Logo /></div>
        <div className="flex items-center gap-2 mb-8">
          {[1, 2, 3, 4].map((s) => (
            <div key={s} className={`h-1.5 flex-1 rounded-full transition-colors ${s <= step ? "bg-primary" : "bg-slate-200"}`} />
          ))}
        </div>

        <div className="bg-white border border-border rounded-2xl p-7 md:p-9">
          {step === 1 && (
            <div className="animate-rise">
              <h2 className="font-display text-2xl font-extrabold text-slate-900">Votre entreprise</h2>
              <p className="text-slate-500 text-sm mt-1 mb-6">Présentez-vous en quelques mots pour que nous sélectionnions les bons marchés.</p>
              <label className="text-sm font-medium text-slate-700">Nom de votre entreprise</label>
              <Input value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Ex : Studio Lumière, SARL Toituri…" className="mt-2 mb-5" data-testid="onboarding-company-input" />
              <label className="text-sm font-medium text-slate-700">Votre activité en une phrase <span className="text-slate-400">(facultatif)</span></label>
              <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} placeholder="Ex : agence de communication spécialisée dans l'événementiel institutionnel." className="mt-2 mb-6" data-testid="onboarding-description-input" />
              <label className="text-sm font-medium text-slate-700">Votre secteur</label>
              <div className="grid gap-3 mt-2">
                {meta.niches.map((n) => {
                  const Icon = ICONS[n.key] || Megaphone;
                  return (
                    <button key={n.key} onClick={() => setNiche(n.key)} data-testid={`onboarding-niche-${n.key}`}
                      className={`flex items-center gap-4 p-4 rounded-xl border text-left transition-all ${niche === n.key ? "border-primary bg-blue-50/60 ring-1 ring-primary" : "border-border hover:border-slate-300"}`}>
                      <div className={`w-11 h-11 rounded-lg flex items-center justify-center shrink-0 ${niche === n.key ? "bg-primary text-white" : "bg-slate-100 text-slate-500"}`}><Icon className="w-6 h-6" /></div>
                      <div><p className="font-semibold text-slate-900">{n.label}</p></div>
                      {niche === n.key && <Check className="w-5 h-5 text-primary ml-auto" />}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="animate-rise">
              <h2 className="font-display text-2xl font-extrabold text-slate-900">Votre zone géographique</h2>
              <p className="text-slate-500 text-sm mt-1 mb-5">Sélectionnez vos départements. Laissez vide pour couvrir toute la France.</p>
              <label className="text-sm font-medium text-slate-700">Votre ville ou lieu de travail <span className="text-slate-400">(facultatif)</span></label>
              <Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Ex : Rouen" className="mt-2 mb-5" data-testid="onboarding-location-input" />
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-72 overflow-y-auto pr-1">
                {Object.entries(meta.departements).map(([code, name]) => (
                  <button key={code} onClick={() => toggleDep(code)} data-testid={`onboarding-dep-${code}`}
                    className={`text-left px-3 py-2 rounded-lg border text-sm transition-all ${deps.includes(code) ? "border-primary bg-blue-50 text-primary font-semibold" : "border-border text-slate-600 hover:border-slate-300"}`}>
                    <span className="font-mono text-xs">{code}</span> {name}
                  </button>
                ))}
              </div>
              <p className="text-xs text-slate-400 mt-3">{deps.length === 0 ? "France entière" : `${deps.length} département(s) sélectionné(s)`}</p>
            </div>
          )}

          {step === 3 && (
            <div className="animate-rise">
              <h2 className="font-display text-2xl font-extrabold text-slate-900">La taille de vos marchés</h2>
              <p className="text-slate-500 text-sm mt-1 mb-6">Quels montants vous intéressent le plus ?</p>
              <div className="grid gap-3">
                {BUDGETS.map((b) => (
                  <button key={b.key} onClick={() => setBudget(b.key)} data-testid={`onboarding-budget-${b.key}`}
                    className={`flex items-center gap-4 p-4 rounded-xl border text-left transition-all ${budget === b.key ? "border-primary bg-blue-50/60 ring-1 ring-primary" : "border-border hover:border-slate-300"}`}>
                    <div><p className="font-semibold text-slate-900">{b.label}</p><p className="text-xs text-slate-500">{b.desc}</p></div>
                    {budget === b.key && <Check className="w-5 h-5 text-primary ml-auto" />}
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="animate-rise">
              <h2 className="font-display text-2xl font-extrabold text-slate-900">Vos alertes</h2>
              <p className="text-slate-500 text-sm mt-1 mb-6">Choisissez quand recevoir votre sélection. Vous pourrez tout modifier plus tard.</p>
              <label className="text-sm font-medium text-slate-700">Fréquence</label>
              <div className="grid grid-cols-2 gap-3 mt-2 mb-6">
                {[{ k: "daily", l: "Chaque jour", d: "Du lundi au vendredi" }, { k: "weekly", l: "Une fois par semaine", d: "Le lundi matin" }].map((f) => (
                  <button key={f.k} onClick={() => setFrequency(f.k)} data-testid={`onboarding-freq-${f.k}`}
                    className={`p-4 rounded-xl border text-left transition-all ${frequency === f.k ? "border-primary bg-blue-50/60 ring-1 ring-primary" : "border-border hover:border-slate-300"}`}>
                    <p className="font-semibold text-slate-900">{f.l}</p><p className="text-xs text-slate-500">{f.d}</p>
                  </button>
                ))}
              </div>
              <label className="text-sm font-medium text-slate-700 flex items-center gap-1.5"><Clock className="w-4 h-4 text-primary" /> Heure d'envoi</label>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {SEND_HOURS.map((h) => (
                  <button key={h.v} onClick={() => setSendHour(h.v)} data-testid={`onboarding-hour-${h.v}`}
                    className={`px-3 py-2.5 rounded-lg border text-sm font-medium transition-all ${sendHour === h.v ? "border-primary bg-blue-50 text-primary" : "border-border text-slate-600 hover:border-slate-300"}`}>
                    {h.l}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between mt-8">
            {step > 1 ? (
              <Button variant="ghost" onClick={() => setStep(step - 1)} data-testid="onboarding-back-btn"><ArrowLeft className="w-4 h-4 mr-1" /> Retour</Button>
            ) : <span />}
            {step < 4 ? (
              <Button onClick={() => canNext && setStep(step + 1)} disabled={!canNext} className="rounded-full" data-testid="onboarding-next-btn">Continuer <ArrowRight className="w-4 h-4 ml-1" /></Button>
            ) : (
              <Button onClick={submit} disabled={saving} className="rounded-full" data-testid="onboarding-finish-btn">{saving ? "Enregistrement…" : "Voir mes marchés"} <ArrowRight className="w-4 h-4 ml-1" /></Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
